<template>
    <CompassDialog
        :visible.sync="showModal"
        :title="$t.__('developerPage.ApiClientForm.creating_api_access')"
        class="api-client-form"
        @closed="close"
    >
        <template slot="default">
            <div class="dialog__default">
                <div v-if="!oauthClient">
                    <el-form
                        ref="apiAccessForm"
                        :model="clientData"
                        :inlineMessage="true"
                        labelPosition="top"
                    >
                        <el-form-item class="mb-2 bold" :error="errors.get('type')" prop="type">
                            <h3>{{ $l10n.__("Type d'accès") }}</h3>
                            <div class="separator mb-5"></div>
                            <el-radio
                                v-model="clientData.type"
                                class="mb-3"
                                label="private"
                                :disabled="isEditedClient"
                            >
                                <strong>{{ $l10n.__('Privé') }}</strong>
                                <p class="grey mt-2 pl-4">
                                    {{
                                        $l10n.__(
                                            'Accès API limité aux collaborateurs de votre compte SELLSY.',
                                        )
                                    }}<br />
                                    {{
                                        $l10n.__(
                                            'Un flow "OAuth 2 authorization code" sera requis.',
                                        )
                                    }}
                                </p>
                            </el-radio>
                            <el-radio
                                v-model="clientData.type"
                                class="mb-3"
                                label="personal"
                                :disabled="isEditedClient"
                            >
                                <strong>{{ $l10n.__('Personnel') }}</strong>
                                <p class="grey mt-2 pl-4">
                                    {{ $l10n.__('Accès API lié à un collaborateur.') }}<br />
                                    {{
                                        $l10n.__(
                                            'L\'utilisation du flow "OAuth 2 client credentials" est possible.',
                                        )
                                    }}
                                </p>
                            </el-radio>
                            <el-radio
                                v-model="clientData.type"
                                class="mb-2"
                                label="public"
                                :disabled="isEditedClient"
                            >
                                <strong>{{ $l10n.__('Publique') }}</strong>
                                <p class="grey mt-2 pl-4">
                                    {{
                                        $l10n.__(
                                            'Accès API permettant de se connecter à tout les comptes SELLSY',
                                        )
                                    }}<br />
                                    {{
                                        $l10n.__(
                                            'Une validation de SELLSY et un flow "OAuth 2 authorization code" seront requises.',
                                        )
                                    }}
                                </p>
                            </el-radio>
                        </el-form-item>
                    </el-form>
                    <div class="separator mb-4"></div>
                </div>
                <div v-if="!oauthClient && clientData.type">
                    <el-form
                        ref="accessTokenForm"
                        :model="clientData"
                        :inlineMessage="true"
                        :rules="rules"
                        labelPosition="top"
                    >
                        <el-form-item
                            :error="errors.get('name')"
                            prop="name"
                            :label="$t.__('developerPage.ApiClientForm.access_name')"
                            class="mb-4"
                            style="font-size: 20px"
                        >
                            <el-input v-model="clientData.name" class="wp-10" maxlength="150" />
                        </el-form-item>
                        <el-form-item
                            v-if="clientData.type != 'personal'"
                            :error="errors.get('redirectUri')"
                            prop="redirectUri"
                            :label="$t.__('developerPage.ApiClientForm.redirect_link')"
                            class="mb-4"
                        >
                            <el-input
                                v-model="clientData.redirectUri"
                                type="textarea"
                                class="wp-10"
                                :autosize="{ minRows: 2, maxRows: 4 }"
                                :placeholder="
                                    $t.__('developerPage.ApiClientForm.enter_one_redirect_url')
                                "
                            />
                        </el-form-item>

                        <el-form-item
                            v-if="clientData.type == 'personal'"
                            :error="errors.get('staffId')"
                            prop="staffId"
                            :label="$t.__('developerPage.ApiClientForm.select_linked_staff_to_api_access')"
                            class="mb-4"
                        >
                            <autocomplete-input
                                v-model="clientData.staffId"
                                mode="edit"
                                :placeholder="$t.__('developerPage.ApiClientForm.linked_staff')"
                                :options="staffs"
                                :multiple="false"
                                labelColumn="fullName"
                                idColumn="id"
                            />
                        </el-form-item>

                            <el-form-item
                                v-if="clientData.type == 'personal'"
                                :label="$t.__('developerPage.ApiClientForm.remote_access')"
                            >
                                <p class="grey">
                                    {{
                                        $l10n.__(
                                            "Cette fonctionnalité vous permet d'emprunter l'identité d'autres collaborateurs sur un même accès API. En passant le header `X-Switch-User` dans vos appels, ceux ci seront joués pour le compte du collaborateur passé en valeur de ce header.",
                                        )
                                    }}
                                </p>
                                <p class="grey">
                                    {{
                                        $l10n.__(
                                            "A noter que seuls des collaborateurs avec le rôle administrateur peuvent générer ce type d'accès et emprunter ainsi l'identité de leurs collaborateurs.",
                                        )
                                    }}
                                </p>
                                <p class="bold grey">
                                    {{
                                        $l10n.__(
                                            "Attention à n'utiliser cette option que si nécéssaire",
                                        )
                                    }}
                                </p>
                                <div class="flex-level ai-center mt-2">
                                    <CompassSwitch
                                        v-model="clientData.delegated"
                                        :disabled="!isAdminAccess"
                                        size="medium"
                                    />
                                    <label class="ml-2">{{
                                        $l10n.__("Activer l'accès délégué")
                                    }}</label>
                                </div>
                            </el-form-item>

                            <el-form-item
                                v-if="clientData.type !== 'personal'"
                                :label="
                                    $t.__(
                                        'developerPage.ApiClientForm.block_simultaneous_connections',
                                    )
                                "
                            >
                                <p class="grey">
                                    {{
                                        $l10n.__(
                                            "Lorsqu'un nouvel access token est demandé pour cet accès par un utilisateur, tous ceux précédemment générés par ce même utilisateur seront révoqués",
                                        )
                                    }}
                                </p>
                                <div class="flex-level ai-center mt-2">
                                    <CompassSwitch v-model="clientData.singleLogin" size="medium" />
                                    <label class="ml-2">{{
                                        $l10n.__('Bloquer les connexions simultanées')
                                    }}</label>
                                </div>
                            </el-form-item>

                            <el-form-item :error="errors.get('scopes')" class="mb-4" prop="scopes">
                                <p class="bold fs-5 mb-3">
                                    {{
                                        $l10n.__(
                                            'Choisissez les autorisations que vous voulez donner à votre accès API ',
                                        )
                                    }}
                                </p>
                                <input-scopes
                                    v-model="clientData.scopes"
                                    :scopes="scopesTree"
                                    :handleChecked="handleChecked"
                                />
                            </el-form-item>
                            <div class="aright mt-5">
                                <CompassButton
                                    :label="$t.__('common.actions.cancel')"
                                    type="secondary"
                                    @click="close"
                                />
                                <CompassButton
                                    :label="$t.__('common.actions.save')"
                                    type="primary"
                                    @click.prevent="submitForm('accessTokenForm')"
                                />
                            </div>
                        </el-form>
                    </div>
                    <div v-else-if="oauthClient" class="mx-auto mt-4 wp-5">
                        <p class="bold">
                            {{ $l10n.__('Ci-dessous le client oauth que vous avez généré.') }}
                        </p>
                        <div class="alert alert-warning">
                            <p>
                                {{
                                    $l10n.__(
                                        'Assurez-vous de copier ces information maintenant. Vous ne serez pas en mesure de le voir à nouveau !',
                                    )
                                }}
                            </p>
                        </div>
                        <p class="bold">Client id</p>
                        <div class="copy-element">
                            <pre class="token well">{{ oauthClient.id }}</pre>
                            <SaasClipboard v-if="oauthClient.id" :data="oauthClient.id" />
                        </div>
                        <p class="bold">Client secret</p>
                        <div class="copy-element">
                            <pre class="token well">{{ oauthClient.secret }}</pre>
                            <SaasClipboard v-if="oauthClient.secret" :data="oauthClient.secret" />
                        </div>
                        <div class="aright mt-5">
                            <CompassButton
                                :label="$t.__('common.actions.close')"
                                type="primary"
                                @click="close"
                            />
                        </div>
                    </div>
                </slot>
            </div>
        </template>
    </CompassDialog>
</template>
<script>
import FormMixins from '../../mixins/FormMixins';
import ApiClientRules from './rules/ApiClientRules';
import AutocompleteInput from 'vcomponents/inputs/AutocompleteInput.vue';
import SaasClipboard from 'vcomponents/utilities/SaasClipboard.vue';
import { createNamespacedHelpers } from 'vuex';
import ScopesInput from './ScopesInput.vue';
const { mapState } = createNamespacedHelpers('user');

export default {
    components: {
        SaasClipboard,
        'autocomplete-input': AutocompleteInput,
        'input-scopes': ScopesInput,
    },
    mixins: [FormMixins],

    props: {
        isVisible: {
            type: Boolean,
            required: true,
        },
        staffs: {
            type: Array,
            required: true,
        },
        scopes: {
            type: [Object, Array],
            required: true,
        },
        scopesTree: {
            type: Object,
            required: true,
        },
        editedClient: {
            type: Object,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            showModal: this.isVisible,
            rules: ApiClientRules,
            clientData: {},
            oauthClient: null,
            loading: false,
            parentScopes: {}, //first parent for each scopes
            childScopes: {}, //first childs for each scopes
        };
    },
    computed: {
        ...mapState({
            userId: 'id',
        }),
        isAdminAccess() {
            if (!this.clientData.staffId) {
                return false;
            }

            for (const staff of this.staffs) {
                if (staff.id != this.clientData.staffId) {
                    continue;
                }
                return staff.isAdmin;
            }

            return false;
        },
        isEditedClient() {
            return this.editedClient !== null;
        },
    },
    created() {
        this.initClientData();
        this.clientData.staffId = parseInt(this.userId);
        this.clientData.scopes = this.scopes;
        this.buildTrees(this.scopesTree); //build childScopes and parentScopes
    },
    mounted() {
        if (this.editedClient) {
                this.editedClient.redirectUri = this.editedClient.redirect_uri.join('\n');
                this.clientData = _.cloneDeep(this.editedClient);
                this.clientData.staffId =
                    this.clientData.user !== null ? parseInt(this.clientData.user.id) : null;
                this.selectChildScopes(this.clientData.scopes);
                this.clientData.scopes = [...new Set(this.clientData.scopes)];
            } else {
                this.initClientData();
            }
    },
    methods: {
        initClientData() {
            this.clientData = {
                name: '',
                scopes: this.scopes,
                staffId: parseInt(this.userId),
                type: null,
                redirectUri: '',
                delegated: false,
                singleLogin: false,
            };
        },
        selectChildScopes(scopes) {
            scopes.forEach((scope) => {
                if (scope in this.childScopes) {
                    this.clientData.scopes.push(...this.childScopes[scope]);
                    this.selectChildScopes(this.childScopes[scope]);
                }
            });
        },
        buildTrees(scopes, parent) {
            if (scopes !== null && (typeof scopes === 'object' || Array.isArray(scopes))) {
                if (Array.isArray(scopes)) {
                    scopes.forEach((value) => {
                        this.buildTrees(value, parent);
                    });
                } else {
                    //scope[0] => key, scope[1] => value
                    Object.entries(scopes).forEach((scope) => {
                        //special trick for activities scopes group
                        const key = Number.isInteger(parseInt(scope[0])) ? scope[1] : scope[0];

                        if (parent) {
                            this.buildParentTree(key, parent);
                        }

                        if (!Number.isInteger(parseInt(scope[0]))) {
                            this.buildChildTree(key, scope[1]);
                            this.buildTrees(scope[1], key);
                        }
                    });
                }
            } else {
                if (parent) {
                    this.buildParentTree(scopes, parent);
                }
            }
        },
        close() {
            this.clientData = this.initClientData();
            this.oauthClient = null;

            this.$emit('close-form');
        },
        async save() {
            this.loading = true;

            try {
                let data = {
                    name: this.clientData.name,
                    scopes: this.clientData.scopes,
                    user_id: null,
                    redirect_uri: [],
                    single_login: this.clientData.singleLogin,
                };

                // context by client type
                switch (this.clientData.type) {
                    case 'personal':
                        data.user_id = this.clientData.staffId;
                        if (this.isAdminAccess) data.delegated = this.clientData.delegated;
                        break;
                    case 'private':
                    case 'public':
                        data.redirect_uri = this.clientData.redirectUri.trim().split('\n');
                        break;
                }

                if (!this.editedClient) {
                    //Create token
                    data.type = this.clientData.type;
                    let response = await ApiClient.post('/clients', {}, data);

                    this.$emit('create', response.data);
                    this.oauthClient = response.data;
                } else {
                    //Update token
                    let response = await ApiClient.put(
                        '/clients/' + this.editedClient.id,
                        {},
                        data,
                    );

                    this.$emit('update', response.data);
                    this.close();
                }
            } catch (error) {
                //Create token error
                if (
                    error.response &&
                    error.response.status == 422 &&
                    error.response.data.error &&
                    error.response.data.error.message.includes('name: This name is already use.')
                ) {
                    this.errors.setOneError(
                        'name',
                        this.$l10n.__('Un accès du même nom existe déjà'),
                    );
                } else {
                    Push.error(this.$l10n.__("Problème lors de la création de l'accès"));
                }
            }

            this.loading = false;
        },
        //method passed as prop to recursive component scopes-input  (
        // More infos at :https://www.digitalocean.com/community/tutorials/vuejs-communicating-recursive-components)
        handleChecked(key, value, isChecked, triggeredByDOM) {
            if (typeof value === 'object' || Array.isArray(value)) {
                //if we are on a node (groups) of  scope
                if (typeof value === 'object') {
                    this.updateScopes(key, isChecked, triggeredByDOM);

                    Object.entries(value).forEach((data) => {
                        this.handleChecked(data[0], data[1], isChecked);
                    });
                } else {
                    array.forEach((element, index) => {
                        this.handleChecked(index, element, isChecked);
                    });
                }
            } else {
                this.updateScopes(value, isChecked, triggeredByDOM);
            }
        },
        buildParentTree(currentScope, parentScope) {
            this.parentScopes[currentScope] = parentScope;
        },
        buildChildTree(currentScope, childScope) {
            //init array
            if (!this.childScopes[currentScope]) {
                this.childScopes[currentScope] = [];
            }

            if (childScope === null) {
                return;
            }

            if (Array.isArray(childScope) || typeof childScope === 'object') {
                if (typeof childScope === 'object') {
                    Object.entries(childScope).forEach((data) => {
                        //if key is numeric, take value
                        if (Number.isInteger(parseInt(data[0]))) {
                            this.childScopes[currentScope].push(data[1]);
                        } else {
                            this.childScopes[currentScope].push(data[0]);
                        }
                    });
                } else {
                    this.childScopes[currentScope].push(...childScope);
                }
            } else {
                this.childScopes[currentScope].push(childScope);
            }
        },
        updateScopes(scope, isChecked, triggeredByDOM) {
            let index = this.clientData.scopes.indexOf(scope);
            let parent = this.parentScopes[scope];
            if (isChecked) {
                //check if scope was not already in list
                if (index === -1) {
                    this.clientData.scopes.push(scope);
                }

                if (parent && triggeredByDOM) {
                    //check recurcively if every childs of parent is checked , if yes, add parent too
                    let checkChildsScope = (parent) => {
                        let childs = this.childScopes[parent];

                        if (childs) {
                            let checkParent = true;
                            childs.forEach((childScope) => {
                                //dont check parent if every child are not checked
                                if (!this.clientData.scopes.includes(childScope)) {
                                    checkParent = false;
                                }
                            });

                            if (checkParent && !this.clientData.scopes.includes(parent)) {
                                this.clientData.scopes.push(parent);
                            }

                            if (this.parentScopes[parent]) {
                                checkChildsScope(this.parentScopes[parent]);
                            }
                        }
                    };

                    checkChildsScope(parent);
                }
            } else {
                if (index !== -1) {
                    //remove scope if present
                    this.clientData.scopes.splice(index, 1);

                    //remove parent scope too
                    if (parent) {
                        this.updateScopes(parent, false);
                    }
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

.api-client-form {
    .dialog {
        &__default {
            padding-bottom: 24px;

            h3 {
                margin: 0;
            }
        }
    }
}
pre.token {
    word-break: break-all;
    white-space: normal;
}
.separator {
    position: relative;

    &::before {
        content: ' ';
        position: absolute;
        width: 100%;
        border-bottom: solid $border-color 1px;
    }
}
</style>
